import { useEffect, useState, useCallback } from 'react'

type ServiceStatus = 'checking' | 'ok' | 'error'

type HealthStatus = {
  api: ServiceStatus
  db: ServiceStatus
  lastChecked: Date | null
}

const POLL_INTERVAL_MS = 10_000

export function HealthPage() {
  const [status, setStatus] = useState<HealthStatus>({
    api: 'checking',
    db: 'checking',
    lastChecked: null,
  })

  const check = useCallback(async () => {
    const [apiResult, dbResult] = await Promise.allSettled([
      fetch('/api/public/health').then((r) => r.json()),
      fetch('/api/public/health/db').then((r) => r.json()),
    ])

    setStatus({
      api: apiResult.status === 'fulfilled' ? 'ok' : 'error',
      db:
        dbResult.status === 'fulfilled' &&
        (dbResult.value as { status: string }).status === 'ok'
          ? 'ok'
          : 'error',
      lastChecked: new Date(),
    })
  }, [])

  useEffect(() => {
    check()
    const interval = setInterval(check, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [check])

  const dot = (s: ServiceStatus) => {
    if (s === 'ok') return <span className="text-emerald-400">●</span>
    if (s === 'error') return <span className="text-red-400">●</span>
    return <span className="text-zinc-500 animate-pulse">●</span>
  }

  const lastCheckedLabel = status.lastChecked
    ? status.lastChecked.toLocaleTimeString()
    : 'checking...'

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-base">
      <div className="bg-surface-overlay border border-border-default rounded-xl p-8 w-80">
        <div className="flex items-center gap-3 mb-6">
          <span className="text-brand-orange text-2xl font-semibold">
            Gear<span className="text-white">Locker</span>
          </span>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400">API</span>
            <span className="flex items-center gap-2">
              {dot(status.api)}
              <span className="text-zinc-300 capitalize">{status.api}</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Database</span>
            <span className="flex items-center gap-2">
              {dot(status.db)}
              <span className="text-zinc-300 capitalize">{status.db}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border-default">
          <p className="text-zinc-600 text-xs">
            v{import.meta.env.VITE_GEARLOCKER_WEB_VERSION ?? '0.1.0'}
          </p>
          <p className="text-zinc-600 text-xs">
            Last checked {lastCheckedLabel}
          </p>
        </div>
      </div>
    </div>
  )
}
